// Quick-status picker pill — used on the property detail page in place of
// the read-only PropertyStatusBadge for users who can change the status
// (assigned broker, OWNER / MANAGER roles). One tap opens a Popover on
// desktop or a bottom Sheet on mobile with the 4 status options and
// fires the change mutation. Toast on success / localised error on failure.
//
// Tracks design source:
//   /tmp/design-files/realestate/project/estate-quick-status.jsx
//     - EstateQuickStatusMobile  (action sheet variant)
//     - EstateQuickStatusDesktop (popover variant)
//   /tmp/design-files/realestate/project/estate-property-detail-v2.jsx
//     - the dashed-border pill in the badge row (mobile + desktop hero)

import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronRight, Clock } from "lucide-react";

import { cn } from "@/lib/utils";
import { localizeError } from "@/lib/errorMessages";
import { ApiError } from "@/lib/api-client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { useChangePropertyStatus } from "@/features/property/api";
import { formatRelativeVi } from "@/features/property/utils/relative-time";
import type { Property, PropertyStatus } from "@/types/property";

// ---------------------------------------------------------------------------
// Status taxonomy (mirrors the design comp). Keys match the backend's
// PropertyStatus enum exactly so we can pass them straight to the mutation.
// ---------------------------------------------------------------------------

interface StatusOption {
  key: PropertyStatus;
  label: string;
  desc: string;
  /** Tailwind classes for the dot in the pill + picker rows. */
  dot: string;
  /** Tailwind classes for the pill background + text + dashed border. */
  pillBg: string;
  pillText: string;
  pillBorder: string;
  /** Tailwind classes for the picker row's "current" highlight surface. */
  rowBg: string;
  rowBorder: string;
  rowText: string;
}

const STATUS_OPTIONS: readonly StatusOption[] = [
  {
    key: "AVAILABLE",
    label: "Còn trống",
    desc: "Đang chào bán/cho thuê công khai",
    dot: "bg-success",
    pillBg: "bg-success-soft",
    pillText: "text-success",
    pillBorder: "border-success/40",
    rowBg: "bg-success-soft",
    rowBorder: "border-success/30",
    rowText: "text-success",
  },
  {
    key: "DEPOSIT",
    label: "Đã cọc",
    desc: "Khách đã đặt cọc, chờ hoàn tất",
    dot: "bg-warning",
    pillBg: "bg-warning-soft",
    pillText: "text-warning",
    pillBorder: "border-warning/40",
    rowBg: "bg-warning-soft",
    rowBorder: "border-warning/30",
    rowText: "text-warning",
  },
  {
    key: "SOLD",
    label: "Đã bán",
    desc: "Giao dịch bán đã hoàn thành",
    dot: "bg-slate-500",
    pillBg: "bg-slate-100",
    pillText: "text-slate-700",
    pillBorder: "border-slate-500/40",
    rowBg: "bg-slate-100",
    rowBorder: "border-slate-500/30",
    rowText: "text-slate-700",
  },
  {
    key: "RENTED",
    label: "Đã thuê",
    desc: "Đã ký hợp đồng cho thuê",
    dot: "bg-sky-500",
    pillBg: "bg-sky-50",
    pillText: "text-sky-700",
    pillBorder: "border-sky-500/40",
    rowBg: "bg-sky-50",
    rowBorder: "border-sky-500/30",
    rowText: "text-sky-700",
  },
] as const;

function optionFor(status: PropertyStatus): StatusOption {
  // Non-null assertion is safe — STATUS_OPTIONS covers every PropertyStatus
  // enum value (compile-time exhaustive via the union type).
  return STATUS_OPTIONS.find((o) => o.key === status)!;
}

// Terminal states the design rule treats as read-only for visual hygiene.
// The backend's transition table still allows other transitions; this is
// a UX nicety, not a security boundary.
const TERMINAL_STATUSES: readonly PropertyStatus[] = ["SOLD", "RENTED"] as const;

interface PropertyQuickStatusButtonProps {
  property: Property;
  /** When true the pill renders as a read-only badge with no chevron and
   *  no popover. Default false — the page passes true for non-broker users. */
  readOnly?: boolean | undefined;
  className?: string | undefined;
}

export default function PropertyQuickStatusButton({
  property,
  readOnly = false,
  className,
}: PropertyQuickStatusButtonProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [open, setOpen] = useState(false);
  const mutation = useChangePropertyStatus(property.id);

  const current = optionFor(property.status);
  const terminal = TERMINAL_STATUSES.includes(property.status);
  // Strip the chevron + disable the popover when the state is terminal or
  // the caller marks this read-only.
  const interactive = !readOnly && !terminal;

  const handleSelect = (next: PropertyStatus) => {
    if (next === property.status) {
      setOpen(false);
      return;
    }
    const previousStatus = property.status;
    const nextLabel = optionFor(next).label;
    // Stable description shape — internal code (or "BĐS" fallback) + the
    // "vừa xong" affordance that matches the design's two-line toast.
    const descriptionPrefix =
      property.internalCode !== null
        ? property.internalCode
        : "BĐS";
    mutation.mutate(next, {
      onSuccess: () => {
        toast.success(`Đã chuyển sang ${nextLabel}`, {
          description: `${descriptionPrefix} · vừa xong`,
          action: {
            label: "Hoàn tác",
            onClick: () => {
              // Revert by re-issuing the mutation with the previous status.
              // We deliberately do NOT show a "Đã hoàn tác" toast on success
              // — Sonner already closes the original toast on click, and a
              // chained toast would feel noisy.
              mutation.mutate(previousStatus, {
                onError: (error: unknown) => {
                  toast.error(localizeError(error));
                },
              });
            },
          },
        });
      },
      onError: (error) => {
        if (
          error instanceof ApiError &&
          error.body?.code === "PROPERTY_INVALID_STATUS_TRANSITION"
        ) {
          toast.error("Không thể chuyển trạng thái này");
          return;
        }
        toast.error(localizeError(error));
      },
    });
    setOpen(false);
  };

  // Pill visual — same shape for interactive + read-only; only the dashed
  // border + chevron flip.
  const pill = (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-[3px] text-[11px] font-semibold whitespace-nowrap",
        current.pillBg,
        current.pillText,
        "border",
        interactive
          ? cn("border-dashed cursor-pointer", current.pillBorder)
          : "border-solid border-border",
        className,
      )}
    >
      <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", current.dot)} />
      <span>{current.label}</span>
      {interactive ? (
        <ChevronDown aria-hidden="true" className="size-3 opacity-70" />
      ) : null}
    </span>
  );

  if (!interactive) {
    return (
      <span aria-label={`Trạng thái: ${current.label}`} role="status">
        {pill}
      </span>
    );
  }

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Đổi trạng thái — hiện tại: ${current.label}`}
            disabled={mutation.isPending}
            className="inline-flex items-center disabled:cursor-not-allowed disabled:opacity-70"
          >
            {pill}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[320px] p-0"
        >
          <DesktopPickerBody
            property={property}
            currentKey={property.status}
            onSelect={handleSelect}
            disabled={mutation.isPending}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Đổi trạng thái — hiện tại: ${current.label}`}
        disabled={mutation.isPending}
        className="inline-flex items-center disabled:cursor-not-allowed disabled:opacity-70"
      >
        {pill}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="gap-0 rounded-t-[20px] p-0"
        >
          <MobileSheetBody
            property={property}
            currentKey={property.status}
            onSelect={handleSelect}
            disabled={mutation.isPending}
            onClose={() => setOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop popover body — compact rows with colour dot + label + description.
// ---------------------------------------------------------------------------

function DesktopPickerBody({
  property,
  currentKey,
  onSelect,
  disabled,
}: {
  property: Property;
  currentKey: PropertyStatus;
  onSelect: (status: PropertyStatus) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col">
      <div className="border-b border-border px-3.5 py-3">
        <div className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
          Đổi trạng thái
          {property.internalCode !== null ? ` · ${property.internalCode}` : null}
        </div>
        <div className="mt-0.5 text-[14px] font-bold">Chọn trạng thái mới</div>
      </div>

      <div className="flex flex-col gap-0.5 p-1.5">
        {STATUS_OPTIONS.map((opt) => {
          const current = opt.key === currentKey;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSelect(opt.key)}
              disabled={disabled || current}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                current
                  ? cn(opt.rowBg, opt.rowBorder)
                  : "border-transparent hover:bg-muted",
                disabled && !current ? "opacity-60" : "",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "size-2.5 shrink-0 rounded-full",
                  opt.dot,
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[14px] font-semibold">{opt.label}</span>
                  {current ? (
                    <span
                      className={cn(
                        "rounded border px-1 py-px font-mono text-[11px] font-bold",
                        opt.rowBorder,
                        opt.rowText,
                      )}
                    >
                      Hiện tại
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {opt.desc}
                </div>
              </div>
              {current ? (
                <Check
                  aria-hidden="true"
                  className={cn("size-3 shrink-0", opt.rowText)}
                />
              ) : (
                <ChevronRight
                  aria-hidden="true"
                  className="size-3 shrink-0 text-ink-faint"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-1.5 border-t border-border bg-background px-3.5 py-2">
        <Clock aria-hidden="true" className="size-3 text-muted-foreground" />
        <span className="font-mono text-[11px] text-muted-foreground">
          Cập nhật {formatRelativeVi(property.updatedAt)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile sheet body — radio rows with bigger touch targets.
// ---------------------------------------------------------------------------

function MobileSheetBody({
  property,
  currentKey,
  onSelect,
  disabled,
  onClose,
}: {
  property: Property;
  currentKey: PropertyStatus;
  onSelect: (status: PropertyStatus) => void;
  disabled: boolean;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col pb-6">
      {/* Drag handle */}
      <div className="mx-auto mt-2.5 h-1 w-9 rounded-full bg-border-strong" />

      <div className="flex items-center justify-between border-b border-border px-[18px] pt-3 pb-3.5">
        <div>
          <div className="font-mono text-[11px] tracking-wider text-muted-foreground">
            {property.internalCode !== null ? `${property.internalCode}` : "Bất động sản"}
            {property.buildingName !== null
              ? ` · ${property.buildingName}`
              : null}
          </div>
          <SheetTitle className="mt-0.5 text-[20px] font-bold tracking-tight">
            Đổi trạng thái
          </SheetTitle>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="flex size-8 items-center justify-center rounded-lg border border-border bg-card text-foreground hover:bg-muted"
        >
          <span aria-hidden="true" className="text-base leading-none">
            ×
          </span>
        </button>
      </div>

      <div className="flex flex-col gap-1.5 px-3 py-3">
        {STATUS_OPTIONS.map((opt) => {
          const current = opt.key === currentKey;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSelect(opt.key)}
              disabled={disabled || current}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors",
                current
                  ? cn(opt.rowBg, opt.rowBorder)
                  : "border-border bg-card shadow-[var(--shadow-soft)]",
                disabled && !current ? "opacity-60" : "",
              )}
            >
              {/* Radio */}
              <span
                aria-hidden
                className={cn(
                  "flex size-[18px] shrink-0 items-center justify-center rounded-full border-2",
                  current ? cn(opt.dot, "border-transparent") : "border-border-strong",
                )}
              >
                {current ? (
                  <span className="size-1.5 rounded-full bg-white" />
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className={cn("size-2 shrink-0 rounded-full", opt.dot)}
                  />
                  <span
                    className={cn(
                      "text-[14px] font-semibold",
                      current ? opt.rowText : "text-foreground",
                    )}
                  >
                    {opt.label}
                  </span>
                  {current ? (
                    <span
                      className={cn(
                        "rounded border px-1 py-px font-mono text-[11px] font-bold",
                        opt.rowBorder,
                        opt.rowText,
                      )}
                    >
                      HIỆN TẠI
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-[12px] text-muted-foreground">
                  {opt.desc}
                </div>
              </div>
              {!current ? (
                <ChevronRight
                  aria-hidden="true"
                  className="size-3.5 shrink-0 text-muted-foreground"
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mx-[18px] mb-3 flex items-center gap-2.5 rounded-xl bg-muted px-3 py-2.5">
        <Clock aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
        <div className="text-[12px] text-ink-soft">
          Cập nhật gần nhất:{" "}
          <span className="font-semibold text-foreground">
            {optionFor(currentKey).label}
          </span>{" "}
          · {formatRelativeVi(property.updatedAt)}
        </div>
      </div>

      <p className="px-[18px] text-center text-[12px] leading-relaxed text-ink-faint">
        Bạn là môi giới phụ trách. Chỉ thay đổi trạng thái — không mở form
        chỉnh sửa đầy đủ.
      </p>
    </div>
  );
}
